import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { RefreshCw, Lock, Activity, Clock, BarChart3, Target, Zap, TrendingUp, AlertTriangle, DollarSign, Globe, GitBranch, Radio, Download, Image } from "lucide-react";
import html2canvas from "html2canvas";
import { EventLogSection } from "@/components/dashboard/EventLogSection";
import { InfoTip } from "@/components/dashboard/InfoTip";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend, LineChart, Line
} from "recharts";

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;

type TimePreset = '15m' | '1h' | '4h' | 'today' | '7d' | '30d' | 'custom';

const TIME_PRESETS: { key: TimePreset; label: string; ms?: number }[] = [
  { key: '15m', label: '15 min', ms: 15 * 60 * 1000 },
  { key: '1h', label: '1 hr', ms: 60 * 60 * 1000 },
  { key: '4h', label: '4 hr', ms: 4 * 60 * 60 * 1000 },
  { key: 'today', label: 'Today' },
  { key: '7d', label: '7 days', ms: 7 * 86400000 },
  { key: '30d', label: '30 days', ms: 30 * 86400000 },
  { key: 'custom', label: 'Custom' },
];

function getPresetRange(preset: TimePreset): { from: string; to: string } {
  const now = new Date();
  const to = now.toISOString();
  if (preset === 'today') {
    const start = new Date(now);
    start.setHours(0, 0, 0, 0);
    return { from: start.toISOString(), to };
  }
  const found = TIME_PRESETS.find(p => p.key === preset);
  if (found?.ms) {
    return { from: new Date(Date.now() - found.ms).toISOString(), to };
  }
  return { from: new Date(Date.now() - 7 * 86400000).toISOString(), to };
}

function toLocalDatetimeString(iso: string): string {
  const d = new Date(iso);
  const pad = (n: number) => n.toString().padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function getAutoRefreshMs(preset: TimePreset): number {
  if (preset === '15m' || preset === '1h') return 60 * 1000;
  return 15 * 60 * 1000;
}

interface VolumeRow {
  event: string;
  total: number;
  browser: number;
  server: number;
  webhook: number;
  totalValue: number;
  conversionRate: number | null;
}

interface FbMatchRow {
  event: string;
  count: number;
  email_pct: number;
  phone_pct: number;
  ip_pct: number;
  fbp_pct: number;
  fbc_pct: number;
  external_id_pct: number;
  ua_pct: number;
  ipv6_pct: number;
  fn_pct: number;
  ln_pct: number;
  ct_pct: number;
  st_pct: number;
  zp_pct: number;
  country_pct: number;
  ge_pct: number;
}

interface Ga4Row {
  event: string;
  count: number;
  client_id_pct: number;
  session_id_pct: number;
}

interface CustomRow {
  event: string;
  count: number;
}

interface PlatformDetail {
  destination: string;
  source: string;
  has_email: boolean;
  has_phone: boolean;
  has_ip: boolean;
  has_fbp: boolean;
  has_fbc: boolean;
  has_external_id: boolean;
  has_ua: boolean;
  has_ga_client_id: boolean;
  has_ga_session_id: boolean;
  ip_version: string | null;
  fbc_source?: string | null;
  has_fn: boolean;
  has_ln: boolean;
  has_ct: boolean;
  has_st: boolean;
  has_zp: boolean;
  has_country: boolean;
  has_ge: boolean;
}

interface LogEntry {
  created_at: string;
  event_name: string;
  event_id: string;
  channels: string;
  destinations: string[];
  event_value: number | null;
  event_value_usd: number | null;
  currency: string | null;
  original_value: number | null;
  original_currency: string | null;
  order_id: string | null;
  customer_type: string | null;
  country_code: string | null;
  traffic_source: string | null;
  platform_details?: PlatformDetail[];
  dedup_confirmed?: boolean;
  fb_confirmed?: { browser: boolean; server: boolean };
  ga4_confirmed?: { browser: boolean; server: boolean };
}

interface TimelineEvent {
  t: string;
  e: string;
}

interface CountryEntry {
  code: string;
  count: number;
}

interface CountryTrafficEntry {
  code: string;
  visits: number;
  viewers: number;
  carts: number;
  checkouts: number;
  purchases: number;
}

interface SourceFunnelEntry {
  source: string;
  PageView: number;
  ViewContent: number;
  AddToCart: number;
  InitiateCheckout: number;
  Purchase: number;
}

interface DedupHealth {
  fb: number | null;
  ga4: number | null;
  overall: number | null;
}

interface ExchangeRates {
  rates: Record<string, number>;
  updated_at: string | null;
}

interface DashboardData {
  from: string;
  to: string;
  total_events: number;
  total_rows?: number;
  truncated?: boolean;
  volume: VolumeRow[];
  fb_match_quality: FbMatchRow[];
  ga4: Ga4Row[];
  custom_events: CustomRow[];
  recent_log: LogEntry[];
  timeline_events?: TimelineEvent[];
  country_breakdown?: CountryEntry[];
  country_traffic?: CountryTrafficEntry[];
  dedup_health?: DedupHealth;
  source_funnel?: SourceFunnelEntry[];
  exchange_rates?: ExchangeRates;
}

const FUNNEL_ORDER = ['PageView', 'ViewContent', 'AddToCart', 'InitiateCheckout', 'Purchase'];
const FUNNEL_COLORS = ['hsl(350, 30%, 62%)', 'hsl(350, 35%, 55%)', 'hsl(350, 40%, 48%)', 'hsl(350, 45%, 42%)', 'hsl(158, 64%, 42%)'];

const PIE_COLORS = ['hsl(350, 30%, 62%)', 'hsl(350, 35%, 55%)', 'hsl(350, 40%, 48%)', 'hsl(350, 45%, 42%)', 'hsl(158, 64%, 42%)', 'hsl(210, 60%, 50%)', 'hsl(45, 93%, 47%)'];
const COUNTRY_COLORS = [
  'hsl(350, 30%, 62%)', 'hsl(210, 60%, 50%)', 'hsl(45, 93%, 47%)', 'hsl(158, 64%, 42%)',
  'hsl(280, 50%, 55%)', 'hsl(30, 80%, 55%)', 'hsl(190, 60%, 45%)', 'hsl(0, 70%, 55%)',
];

const SOURCE_COLORS: Record<string, string> = {
  facebook_paid: 'hsl(210, 70%, 50%)',
  facebook_organic: 'hsl(210, 50%, 65%)',
  google_paid: 'hsl(45, 93%, 47%)',
  google_organic: 'hsl(158, 64%, 42%)',
  direct: 'hsl(220, 12%, 55%)',
  referral: 'hsl(280, 50%, 55%)',
  unknown: 'hsl(0, 0%, 70%)',
};

const SOURCE_BADGE_CLASSES: Record<string, string> = {
  facebook_paid: 'bg-blue-100 text-blue-800 border-blue-200',
  facebook_organic: 'bg-blue-50 text-blue-600 border-blue-100',
  google_paid: 'bg-amber-100 text-amber-800 border-amber-200',
  google_organic: 'bg-green-100 text-green-800 border-green-200',
  direct: 'bg-gray-100 text-gray-700 border-gray-200',
  referral: 'bg-purple-100 text-purple-800 border-purple-200',
  unknown: 'bg-gray-50 text-gray-500 border-gray-100',
};

function getSourceLabel(source: string): string {
  const labels: Record<string, string> = {
    facebook_paid: 'FB Paid',
    facebook_organic: 'FB Organic',
    google_paid: 'Google Paid',
    google_organic: 'Google Organic',
    direct: 'Direct',
    referral: 'Referral',
    unknown: 'Unknown',
  };
  return labels[source] || source;
}

function matchColor(pct: number) {
  if (pct >= 80) return 'text-green-600';
  if (pct >= 50) return 'text-amber-500';
  return 'text-red-500';
}

function matchBarColor(pct: number) {
  if (pct >= 80) return 'hsl(158, 64%, 42%)';
  if (pct >= 50) return 'hsl(45, 93%, 47%)';
  return 'hsl(0, 84%, 60%)';
}

const COUNTRY_FLAGS: Record<string, string> = {
  US: '🇺🇸', IL: '🇮🇱', GB: '🇬🇧', AU: '🇦🇺', CA: '🇨🇦', DE: '🇩🇪', FR: '🇫🇷', JP: '🇯🇵', KR: '🇰🇷', IN: '🇮🇳', HR: '🇭🇷', FI: '🇫🇮', NO: '🇳🇴', ES: '🇪🇸', AE: '🇦🇪',
};

// --- Bucketing logic for Timeline chart ---
function getBucketSizeMs(preset: TimePreset, fromMs: number, toMs: number): number {
  switch (preset) {
    case '15m': return 60_000;          // 1 min
    case '1h':  return 5 * 60_000;     // 5 min
    case '4h':  return 15 * 60_000;    // 15 min
    case 'today': return 60 * 60_000;  // 1 hr
    case '7d':  return 86_400_000;     // 1 day
    case '30d': return 86_400_000;     // 1 day
    case 'custom': {
      const span = toMs - fromMs;
      if (span <= 60 * 60_000) return 5 * 60_000;
      if (span <= 4 * 60 * 60_000) return 15 * 60_000;
      if (span <= 24 * 60 * 60_000) return 60 * 60_000;
      return 86_400_000;
    }
    default: return 86_400_000;
  }
}

function formatBucketLabel(ts: number, bucketMs: number): string {
  const d = new Date(ts);
  if (bucketMs >= 86_400_000) return d.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit' });
  return d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
}

interface BucketRow {
  time: string;
  ts: number;
  [key: string]: string | number;
}

function bucketEvents(events: TimelineEvent[], bucketMs: number, fromMs: number, toMs: number): BucketRow[] {
  const buckets: Map<number, Record<string, number>> = new Map();
  for (let t = fromMs; t <= toMs; t += bucketMs) {
    const key = Math.floor(t / bucketMs) * bucketMs;
    if (!buckets.has(key)) {
      buckets.set(key, {});
      for (const name of FUNNEL_ORDER) buckets.get(key)![name] = 0;
    }
  }
  for (const ev of events) {
    const evTs = new Date(ev.t).getTime();
    const key = Math.floor(evTs / bucketMs) * bucketMs;
    if (!buckets.has(key)) {
      buckets.set(key, {});
      for (const name of FUNNEL_ORDER) buckets.get(key)![name] = 0;
    }
    if (FUNNEL_ORDER.includes(ev.e)) {
      buckets.get(key)![ev.e]++;
    }
  }
  return Array.from(buckets.entries())
    .sort((a, b) => a[0] - b[0])
    .map(([ts, counts]) => ({
      time: formatBucketLabel(ts, bucketMs),
      ts,
      ...counts,
    }));
}

export default function Dashboard() {
  // Block search engine indexing + force LTR English mode
  useEffect(() => {
    let meta = document.querySelector('meta[name="robots"]') as HTMLMetaElement | null;
    const prev = meta?.getAttribute('content') || null;
    if (!meta) {
      meta = document.createElement('meta');
      meta.setAttribute('name', 'robots');
      document.head.appendChild(meta);
    }
    meta.setAttribute('content', 'noindex, nofollow');
    document.title = 'Dashboard';

    // Force LTR + English on <html> to prevent RTL/Hebrew CSS from leaking
    const prevDir = document.documentElement.dir;
    const prevLang = document.documentElement.lang;
    document.documentElement.dir = 'ltr';
    document.documentElement.lang = 'en';

    return () => {
      if (prev) meta!.setAttribute('content', prev);
      else meta!.remove();
      document.documentElement.dir = prevDir;
      document.documentElement.lang = prevLang;
    };
  }, []);
  const dashboardRef = useRef<HTMLDivElement>(null);
  const [exporting, setExporting] = useState(false);
  const [password, setPassword] = useState("");
  const [authenticated, setAuthenticated] = useState(false);
  const [dashKey, setDashKey] = useState<string>("");
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null);
  const [activePreset, setActivePreset] = useState<TimePreset>('today');

  const [dateFrom, setDateFrom] = useState(() => getPresetRange('today').from);
  const [dateTo, setDateTo] = useState(() => getPresetRange('today').to);

  const handlePreset = useCallback((preset: TimePreset) => {
    setActivePreset(preset);
    if (preset !== 'custom') {
      const range = getPresetRange(preset);
      setDateFrom(range.from);
      setDateTo(range.to);
    }
  }, []);

  const fetchData = useCallback(async (key: string) => {
    setLoading(true);
    setError(null);
    const maxRetries = 2;
    try {
      for (let attempt = 0; attempt <= maxRetries; attempt++) {
        try {
          let from = dateFrom;
          let to = dateTo;
          if (activePreset !== 'custom') {
            const range = getPresetRange(activePreset);
            from = range.from;
            to = range.to;
          }
          const encodedKey = btoa(unescape(encodeURIComponent(key)));
          const res = await fetch(
            `${SUPABASE_URL}/functions/v1/dashboard-data?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`,
            { headers: { 'x-dashboard-key': encodedKey } }
          );
          if (res.status === 401) {
            setAuthenticated(false);
            sessionStorage.removeItem('dash_key');
            setError("סיסמה שגויה");
            return;
          }
          if (res.status >= 500 && attempt < maxRetries) {
            await new Promise(r => setTimeout(r, 1000 * Math.pow(2, attempt)));
            continue;
          }
          if (!res.ok) throw new Error(`Error ${res.status}`);
          const json = await res.json();
          setData(json);
          setLastRefresh(new Date());
          return;
        } catch (e) {
          if (attempt < maxRetries) {
            await new Promise(r => setTimeout(r, 1000 * Math.pow(2, attempt)));
            continue;
          }
          setError(e instanceof Error ? e.message : "Unknown error");
        }
      }
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo, activePreset]);

  const handleLogin = () => {
    if (!password.trim()) return;
    setDashKey(password);
    setAuthenticated(true);
    sessionStorage.setItem('dash_key', password);
    fetchData(password);
  };

  useEffect(() => {
    const saved = sessionStorage.getItem('dash_key');
    if (saved) {
      setDashKey(saved);
      setAuthenticated(true);
      fetchData(saved);
    }
  }, []);

  useEffect(() => {
    if (!authenticated || !dashKey) return;
    const ms = getAutoRefreshMs(activePreset);
    const interval = setInterval(() => fetchData(dashKey), ms);
    return () => clearInterval(interval);
  }, [authenticated, dashKey, fetchData, activePreset]);

  useEffect(() => {
    if (authenticated && dashKey) fetchData(dashKey);
  }, [dateFrom, dateTo]);

  // Computed data for charts
  const kpiData = useMemo(() => {
    if (!data) return null;
    const fbEvents = data.fb_match_quality.reduce((s, r) => s + r.count, 0);
    const ga4Events = data.ga4.reduce((s, r) => s + r.count, 0);
    const fbRows = data.fb_match_quality;
    let totalWeightedScore = 0, totalWeightedCount = 0;
    for (const r of fbRows) {
      // Weighted average: fbp is most important for Meta dedup (45%),
      // external_id provides cross-session matching (30%), IP helps geo matching (25%)
      const score = (r.ip_pct * 0.25) + (r.fbp_pct * 0.45) + (r.external_id_pct * 0.30);
      totalWeightedScore += score * r.count;
      totalWeightedCount += r.count;
    }
    const avgMatch = totalWeightedCount > 0 ? Math.round(totalWeightedScore / totalWeightedCount) : 0;

    let dropoffStage = '—';
    let dropoffPct = 0;
    for (let i = 1; i < FUNNEL_ORDER.length; i++) {
      const prev = data.volume.find(v => v.event === FUNNEL_ORDER[i - 1])?.total ?? 0;
      const curr = data.volume.find(v => v.event === FUNNEL_ORDER[i])?.total ?? 0;
      if (prev > 0) {
        const drop = Math.round(((prev - curr) / prev) * 100);
        if (drop > dropoffPct) {
          dropoffPct = drop;
          dropoffStage = `${FUNNEL_ORDER[i - 1]} → ${FUNNEL_ORDER[i]}`;
        }
      }
    }

    return { total: data.total_events, fb: fbEvents, ga4: ga4Events, avgMatch, dropoffStage, dropoffPct };
  }, [data]);

  const funnelData = useMemo(() => {
    if (!data) return [];
    return FUNNEL_ORDER.map((name, idx) => {
      const row = data.volume.find(v => v.event === name);
      const count = row?.total ?? 0;
      let label = name;
      if (idx > 0) {
        const prevCount = data.volume.find(v => v.event === FUNNEL_ORDER[idx - 1])?.total ?? 0;
        const pct = prevCount > 0 ? Math.round((count / prevCount) * 100) : 0;
        label = `${name} (${pct}%)`;
      }
      return { name: label, count };
    });
  }, [data]);

  const matchQualityData = useMemo(() => {
    if (!data || data.fb_match_quality.length === 0) return [];
    const totals = { email: 0, phone: 0, ip: 0, fbp: 0, fbc: 0, ext_id: 0, ua: 0, fn: 0, ln: 0, ct: 0, st: 0, zp: 0, country: 0, ge: 0, n: 0 };
    data.fb_match_quality.forEach(r => {
      totals.email += r.email_pct;
      totals.phone += r.phone_pct;
      totals.ip += r.ip_pct;
      totals.fbp += r.fbp_pct;
      totals.fbc += r.fbc_pct;
      totals.ext_id += r.external_id_pct;
      totals.ua += (r.ua_pct ?? 0);
      totals.fn += (r.fn_pct ?? 0);
      totals.ln += (r.ln_pct ?? 0);
      totals.ct += (r.ct_pct ?? 0);
      totals.st += (r.st_pct ?? 0);
      totals.zp += (r.zp_pct ?? 0);
      totals.country += (r.country_pct ?? 0);
      totals.ge += (r.ge_pct ?? 0);
      totals.n++;
    });
    const avg = (v: number) => Math.round(v / totals.n);
    return [
      { param: 'Email', pct: avg(totals.email) },
      { param: 'Phone', pct: avg(totals.phone) },
      { param: 'First Name', pct: avg(totals.fn) },
      { param: 'Last Name', pct: avg(totals.ln) },
      { param: 'City', pct: avg(totals.ct) },
      { param: 'State', pct: avg(totals.st) },
      { param: 'Zip', pct: avg(totals.zp) },
      { param: 'Country', pct: avg(totals.country) },
      { param: 'Gender', pct: avg(totals.ge) },
      { param: 'ext_id', pct: avg(totals.ext_id) },
      { param: 'IP', pct: avg(totals.ip) },
      { param: 'UA', pct: avg(totals.ua) },
      { param: 'fbp', pct: avg(totals.fbp) },
      { param: 'fbc', pct: avg(totals.fbc) },
    ];
  }, [data]);

  const pieData = useMemo(() => {
    if (!data) return [];
    return data.volume
      .filter(v => v.total > 0)
      .map(v => ({ name: v.event, value: v.total }));
  }, [data]);

  const timelineData = useMemo(() => {
    if (!data?.timeline_events?.length) return [];
    const fromMs = new Date(data.from).getTime();
    const toMs = new Date(data.to).getTime();
    const bucketMs = getBucketSizeMs(activePreset, fromMs, toMs);
    return bucketEvents(data.timeline_events, bucketMs, fromMs, toMs);
  }, [data, activePreset]);

  const totalRevenue = useMemo(() => {
    if (!data) return 0;
    const purchaseRow = data.volume.find(v => v.event === 'Purchase');
    return purchaseRow?.totalValue ?? 0;
  }, [data]);

  const dedupHealth = useMemo(() => {
    if (!data?.dedup_health) return null;
    return data.dedup_health;
  }, [data]);

  // Traffic source breakdown for KPI
  const trafficSourceBreakdown = useMemo(() => {
    if (!data?.source_funnel?.length) return [];
    const totalPV = data.source_funnel.reduce((s, r) => s + r.PageView, 0);
    return data.source_funnel
      .filter(r => r.PageView > 0)
      .map(r => ({
        source: r.source,
        label: getSourceLabel(r.source),
        count: r.PageView,
        pct: totalPV > 0 ? Math.round((r.PageView / totalPV) * 100) : 0,
      }))
      .sort((a, b) => b.count - a.count);
  }, [data]);

  const hourlyHeatmap = useMemo(() => {
    if (!data?.timeline_events?.length) return [];
    const hours = new Array(24).fill(0);
    for (const ev of data.timeline_events) {
      const h = new Date(ev.t).getHours();
      hours[h]++;
    }
    const max = Math.max(...hours, 1);
    return hours.map((count, hour) => ({
      hour,
      count,
      intensity: count / max,
    }));
  }, [data]);

  if (!authenticated) {
    return (
      <div dir="ltr" className="min-h-screen flex items-center justify-center bg-background" style={{ fontFamily: "'DM Sans', sans-serif" }}>
        <Card className="w-full max-w-sm">
          <CardHeader className="text-center">
            <Lock className="mx-auto h-8 w-8 text-muted-foreground mb-2" />
            <CardTitle className="text-lg">Tracking Dashboard</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <Input
              type="password"
              placeholder="Password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleLogin()}
            />
            <Button className="w-full" onClick={handleLogin}>Enter</Button>
            {error && <p className="text-sm text-destructive text-center">{error}</p>}
          </CardContent>
        </Card>
      </div>
    );
  }

  const fmtNum = (n: number) => n.toLocaleString('en-US');
  const fmtVal = (v: number) => {
    if (!v) return "—";
    return `$${fmtNum(Math.round(v))}`;
  };
  const fmtOriginal = (v: number, currency?: string) => {
    if (!v) return "";
    const sym = currency === 'ILS' ? '₪' : currency === 'GBP' ? '£' : currency === 'AUD' ? 'A$' : currency === 'CAD' ? 'C$' : '$';
    return `${sym}${fmtNum(Math.round(v))}`;
  };

  return (
    <div ref={dashboardRef} dir="ltr" className="min-h-screen bg-background p-4 md:p-8 max-w-7xl mx-auto space-y-6" style={{ fontFamily: "'DM Sans', sans-serif" }}>
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <Activity className="h-6 w-6 text-primary" />
          <h1 className="text-2xl font-bold text-foreground">Tracking Dashboard</h1>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => {
            if (!data) return;
            const preset = TIME_PRESETS.find(p => p.key === activePreset);
            const report = {
              exported_at: new Date().toISOString(),
              period: { from: data.from, to: data.to, preset: preset?.label ?? activePreset },
              summary: {
                total_events: data.total_events,
                total_db_rows: data.total_rows ?? data.total_events,
                truncated: data.truncated ?? false,
                revenue_usd: totalRevenue,
              },
              funnel_volume: data.volume,
              fb_match_quality: data.fb_match_quality,
              ga4_identity: data.ga4,
              custom_events: data.custom_events,
              dedup_health: data.dedup_health ?? null,
              source_funnel: data.source_funnel ?? [],
              country_breakdown: data.country_breakdown ?? [],
              country_traffic: data.country_traffic ?? [],
              exchange_rates: data.exchange_rates ?? null,
              recent_events: data.recent_log,
            };
            const blob = new Blob([JSON.stringify(report, null, 2)], { type: 'application/json' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            const dateStr = new Date().toISOString().slice(0, 10);
            a.href = url;
            a.download = `pdrn-dashboard-${activePreset}-${dateStr}.json`;
            a.click();
            URL.revokeObjectURL(url);
          }} disabled={!data}>
            <Download className="h-4 w-4 mr-1" />
            Export
          </Button>
          <Button variant="outline" size="sm" onClick={async () => {
            if (!dashboardRef.current || exporting) return;
            setExporting(true);
            try {
              const el = dashboardRef.current;
              // Temporarily expand to full width for clean capture
              const prevMaxW = el.style.maxWidth;
              const prevOverflow = el.style.overflow;
              el.style.maxWidth = '1400px';
              el.style.overflow = 'visible';
              // Expand all collapsed/scrollable sections
              const scrollAreas = el.querySelectorAll('[data-radix-scroll-area-viewport]');
              const prevStyles: { el: HTMLElement; maxHeight: string; overflow: string }[] = [];
              scrollAreas.forEach(sa => {
                const htmlEl = sa as HTMLElement;
                prevStyles.push({ el: htmlEl, maxHeight: htmlEl.style.maxHeight, overflow: htmlEl.style.overflow });
                htmlEl.style.maxHeight = 'none';
                htmlEl.style.overflow = 'visible';
              });
              await new Promise(r => setTimeout(r, 100));
              const canvas = await html2canvas(el, {
                scale: 2,
                useCORS: true,
                backgroundColor: '#F4F9FB',
                width: el.scrollWidth,
                height: el.scrollHeight,
                windowWidth: 1400,
              });
              // Restore
              el.style.maxWidth = prevMaxW;
              el.style.overflow = prevOverflow;
              prevStyles.forEach(s => { s.el.style.maxHeight = s.maxHeight; s.el.style.overflow = s.overflow; });
              const link = document.createElement('a');
              const dateStr = new Date().toISOString().slice(0, 10);
              link.download = `pdrn-dashboard-${activePreset}-${dateStr}.png`;
              link.href = canvas.toDataURL('image/png');
              link.click();
            } catch (e) {
              console.error('Export image failed:', e);
            } finally {
              setExporting(false);
            }
          }} disabled={!data || exporting}>
            <Image className="h-4 w-4 mr-1" />
            {exporting ? 'Capturing…' : 'Screenshot'}
          </Button>
          <Button variant="outline" size="sm" onClick={() => fetchData(dashKey)} disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Quick Select */}
      <div className="flex items-center gap-2 flex-wrap">
        {TIME_PRESETS.map(p => (
          <Button
            key={p.key}
            variant={activePreset === p.key ? 'default' : 'outline'}
            size="sm"
            onClick={() => handlePreset(p.key)}
          >
            {p.label}
          </Button>
        ))}
      </div>

      {activePreset === 'custom' && (
        <div className="flex items-center gap-3 flex-wrap">
          <Input
            type="datetime-local"
            value={toLocalDatetimeString(dateFrom)}
            onChange={e => setDateFrom(new Date(e.target.value).toISOString())}
            className="w-52"
          />
          <span className="text-muted-foreground">→</span>
          <Input
            type="datetime-local"
            value={toLocalDatetimeString(dateTo)}
            onChange={e => setDateTo(new Date(e.target.value).toISOString())}
            className="w-52"
          />
        </div>
      )}

      {lastRefresh && (
        <div className="flex items-center gap-1 text-xs text-muted-foreground">
          <Clock className="h-3 w-3" />
          Last updated: {lastRefresh.toLocaleTimeString()} · Auto-refresh every {activePreset === '15m' || activePreset === '1h' ? '1 min' : '15 min'}
        </div>
      )}

      {error && <p className="text-destructive text-sm">{error}</p>}

      {data?.truncated && (
        <div className="flex items-center gap-2 rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          <AlertTriangle className="h-4 w-4 flex-shrink-0" />
          <span>Data truncated — more than 10,000 events in this period. Narrow the date range for accurate metrics.</span>
        </div>
      )}

      {data && (
        <>
          {/* KPI Cards */}
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-7 gap-4">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-2 text-muted-foreground text-xs mb-1">
                   <BarChart3 className="h-4 w-4 shrink-0" /> Total Events
                   <InfoTip text='Unique user actions (deduplicated). Each action (e.g. AddToCart) creates 4 DB rows across platforms — this counts unique actions only. "X rows in DB" shows the raw count before dedup.' />
                 </div>
                 <p className="text-2xl xl:text-3xl font-bold text-foreground">{fmtNum(kpiData?.total ?? 0)}</p>
                {data.total_rows && data.total_rows !== data.total_events && (
                  <p className="text-xs text-muted-foreground mt-1">{fmtNum(data.total_rows)} rows in DB</p>
                )}
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-2 text-muted-foreground text-xs mb-1">
                   <Zap className="h-4 w-4 shrink-0" /> FB CAPI
                   <InfoTip text="Number of events sent to Facebook via server-side Conversions API. Counted per-destination (not deduplicated) to reflect actual CAPI volume." />
                 </div>
                 <p className="text-2xl xl:text-3xl font-bold text-foreground">{fmtNum(kpiData?.fb ?? 0)}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-2 text-muted-foreground text-xs mb-1">
                   <TrendingUp className="h-4 w-4 shrink-0" /> GA4 MP
                   <InfoTip text="Number of events sent to Google Analytics via Measurement Protocol (server-side). Counted per-destination." />
                 </div>
                 <p className="text-2xl xl:text-3xl font-bold text-foreground">{fmtNum(kpiData?.ga4 ?? 0)}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-2 text-muted-foreground text-xs mb-1">
                   <Target className="h-4 w-4 shrink-0" /> Avg Match
                   <InfoTip text="Weighted FB CAPI match quality: fbp (45%) + external_id (30%) + IP (25%). Weights reflect Meta's signal priority for dedup. Higher = better ad attribution." />
                 </div>
                 <p className={`text-2xl xl:text-3xl font-bold ${matchColor(kpiData?.avgMatch ?? 0)}`}>
                  {kpiData?.avgMatch ?? 0}%
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-2 text-muted-foreground text-xs mb-1">
                   <DollarSign className="h-4 w-4 shrink-0" /> Revenue
                   <InfoTip text="Total purchase revenue converted to USD using fixed exchange rates. Summed from all Purchase events in the selected period." />
                 </div>
                 <p className="text-2xl xl:text-3xl font-bold text-foreground">{totalRevenue > 0 ? `$${fmtNum(Math.round(totalRevenue))}` : '—'}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-2 text-muted-foreground text-xs mb-1">
                   <GitBranch className="h-4 w-4 shrink-0" /> Dedup Health
                   <InfoTip text="Signal completeness: FB = % of CAPI events with at least 2 of 3 key signals (IP, fbp, external_id). GA4 = % of MP events with both client_id + session_id. Higher = better matching." />
                 </div>
                {dedupHealth ? (
                  <>
                    <p className={`text-2xl xl:text-3xl font-bold ${matchColor(dedupHealth.overall ?? 0)}`}>
                      {dedupHealth.overall !== null ? `${dedupHealth.overall}%` : '—'}
                    </p>
                    <div className="flex gap-2 text-xs text-muted-foreground mt-1">
                      <span>FB: {dedupHealth.fb !== null ? `${dedupHealth.fb}%` : '—'}</span>
                      <span>GA4: {dedupHealth.ga4 !== null ? `${dedupHealth.ga4}%` : '—'}</span>
                    </div>
                  </>
                ) : (
                  <p className="text-2xl xl:text-3xl font-bold text-muted-foreground">—</p>
                )}
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-2 text-muted-foreground text-xs mb-1">
                   <AlertTriangle className="h-4 w-4 shrink-0" /> Top Drop-off
                   <InfoTip text="Largest percentage drop between consecutive funnel stages (PageView → ViewContent → AddToCart → InitiateCheckout → Purchase). Shows where most users abandon." />
                 </div>
                 <p className="text-lg font-bold text-foreground">{kpiData?.dropoffPct ?? 0}%</p>
                 <p className="text-xs text-muted-foreground break-words">{kpiData?.dropoffStage ?? '—'}</p>
              </CardContent>
            </Card>
          </div>

          {/* Exchange Rates */}
          {data.exchange_rates && (
            <div className="flex items-center gap-3 flex-wrap text-xs text-muted-foreground border border-border rounded-lg px-4 py-2.5 bg-muted/30">
              <span className="font-medium text-foreground flex items-center gap-1.5">
                <DollarSign className="h-3.5 w-3.5" /> Exchange Rates:
              </span>
              {Object.entries(data.exchange_rates.rates)
                .filter(([c]) => ['ILS', 'GBP', 'AUD', 'CAD'].includes(c))
                .sort(([a], [b]) => a.localeCompare(b))
                .map(([currency, rate]) => (
                  <span key={currency} className="font-mono">
                    1 USD = {Number(rate).toFixed(3)} {currency}
                  </span>
                ))}
              {data.exchange_rates.updated_at ? (
                <span className="text-muted-foreground/70 ml-auto flex items-center gap-1">
                  Updated: {new Date(data.exchange_rates.updated_at).toLocaleString('en-GB', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                  {(() => {
                    const ageHours = (Date.now() - new Date(data.exchange_rates.updated_at).getTime()) / (1000 * 60 * 60);
                    return ageHours > 24 ? (
                      <span className="text-amber-500 flex items-center gap-0.5">
                        <AlertTriangle className="h-3 w-3" /> Stale ({Math.round(ageHours)}h)
                      </span>
                    ) : null;
                  })()}
                </span>
              ) : (
                <span className="text-amber-500 ml-auto flex items-center gap-1">
                  <AlertTriangle className="h-3 w-3" /> Using fallback rates
                </span>
              )}
            </div>
          )}

          {/* Traffic Sources KPI */}
          {trafficSourceBreakdown.length > 0 && (
            <Card>
              <CardHeader><CardTitle className="text-base flex items-center gap-2"><Radio className="h-4 w-4" /> Traffic Sources <InfoTip text="Breakdown of PageView events by traffic source. Detected from UTM parameters, fbclid, referrer, and in-app browser UA. Percentages show share of total page views." /></CardTitle></CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Pie Chart */}
                  <ResponsiveContainer width="100%" height={280}>
                    <PieChart>
                      <Pie
                        data={trafficSourceBreakdown.map(s => ({ name: s.label, value: s.count }))}
                        dataKey="value"
                        nameKey="name"
                        cx="50%"
                        cy="50%"
                        outerRadius={90}
                        innerRadius={45}
                        label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                        labelLine={true}
                      >
                        {trafficSourceBreakdown.map((s, i) => (
                          <Cell key={i} fill={SOURCE_COLORS[s.source] || SOURCE_COLORS.unknown} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value: number) => [fmtNum(value), 'PageViews']} />
                    </PieChart>
                  </ResponsiveContainer>
                  {/* Legend + Numbers */}
                  <div className="flex flex-col justify-center gap-2">
                    {trafficSourceBreakdown.map(s => (
                      <div key={s.source} className="flex items-center justify-between gap-3 px-3 py-2 rounded-lg border">
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: SOURCE_COLORS[s.source] || SOURCE_COLORS.unknown }} />
                          <span className="text-sm font-medium">{s.label}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-lg font-bold font-mono">{fmtNum(s.count)}</span>
                          <span className="text-xs text-muted-foreground w-10 text-right">({s.pct}%)</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Timeline Line Chart */}
          {timelineData.length > 0 && (
            <Card>
              <CardHeader><CardTitle className="text-base flex items-center gap-1.5">Event Timeline <InfoTip text="Deduplicated event counts over time, bucketed by time interval. Each point represents unique user actions, not raw DB rows." /></CardTitle></CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={timelineData} margin={{ left: 10, right: 10, top: 5, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(20, 10%, 90%)" />
                    <XAxis dataKey="time" tick={{ fontSize: 11 }} interval="preserveStartEnd" />
                    <YAxis tick={{ fontSize: 12 }} allowDecimals={false} />
                    <Tooltip />
                    {FUNNEL_ORDER.map((name, i) => (
                      <Line
                        key={name}
                        type="monotone"
                        dataKey={name}
                        stroke={FUNNEL_COLORS[i]}
                        strokeWidth={2}
                        dot={false}
                        activeDot={{ r: 4 }}
                      />
                    ))}
                    <Legend />
                  </LineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          )}

          {/* Source Funnel Chart + Event Funnel */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Source Funnel — Stacked Bar */}
            {data.source_funnel && data.source_funnel.length > 0 && (
              <Card>
                <CardHeader><CardTitle className="text-base flex items-center gap-1.5">Funnel by Traffic Source <InfoTip text="Stacked bar showing how each traffic source converts through the funnel stages. Useful for comparing paid vs organic performance." /></CardTitle></CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={FUNNEL_ORDER.map(step => {
                      const row: Record<string, string | number> = { step };
                      for (const sf of data.source_funnel!) {
                        row[sf.source] = (sf as unknown as Record<string, number>)[step] || 0;
                      }
                      return row;
                    })} margin={{ left: 10, right: 10 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(20, 10%, 90%)" />
                      <XAxis dataKey="step" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 12 }} allowDecimals={false} />
                      <Tooltip />
                      <Legend formatter={(value: string) => getSourceLabel(value)} />
                      {data.source_funnel!.map((sf) => (
                        <Bar key={sf.source} dataKey={sf.source} stackId="a" fill={SOURCE_COLORS[sf.source] || SOURCE_COLORS.unknown} />
                      ))}
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            )}

            {/* Event Funnel (original) */}
            <Card>
              <CardHeader><CardTitle className="text-base flex items-center gap-1.5">Event Funnel <InfoTip text="Conversion funnel from PageView to Purchase. Percentages show step-to-step conversion rate. Based on deduplicated event counts." /></CardTitle></CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={funnelData} layout="vertical" margin={{ left: 40, right: 20 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(20, 10%, 90%)" />
                    <XAxis type="number" tick={{ fontSize: 12 }} />
                    <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={160} />
                    <Tooltip formatter={(value: number) => [fmtNum(value), 'Events']} />
                    <Bar dataKey="count" radius={[0, 6, 6, 0]}>
                      {funnelData.map((_, i) => (
                        <Cell key={i} fill={FUNNEL_COLORS[i]} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>

          {/* Pie Chart + Match Quality */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
             <Card className="overflow-visible">
               <CardHeader><CardTitle className="text-base flex items-center gap-1.5">Event Distribution <InfoTip text="Pie chart showing the proportion of each event type (PageView, ViewContent, etc.) from deduplicated counts." /></CardTitle></CardHeader>
               <CardContent>
                 {pieData.length > 0 ? (
                   <ResponsiveContainer width="100%" height={320}>
                     <PieChart>
                       <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={75} label={({ percent }) => `${(percent * 100).toFixed(0)}%`} labelLine={true}>
                         {pieData.map((_, i) => (
                           <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                         ))}
                       </Pie>
                       <Tooltip />
                       <Legend />
                     </PieChart>
                   </ResponsiveContainer>
                 ) : (
                   <div className="h-[320px] flex items-center justify-center text-muted-foreground text-sm">No events yet</div>
                 )}
              </CardContent>
            </Card>

            {matchQualityData.length > 0 && (
              <Card className="lg:col-span-2">
                <CardHeader><CardTitle className="text-base flex items-center gap-1.5">FB Match Quality (Avg across events) <InfoTip text="Average match rate per parameter across all FB CAPI events. Green (80%+) = strong, Yellow (50-79%) = moderate, Red (below 50%) = needs improvement." /></CardTitle></CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart data={matchQualityData} margin={{ left: 10, right: 10 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(20, 10%, 90%)" />
                      <XAxis dataKey="param" tick={{ fontSize: 12 }} />
                      <YAxis domain={[0, 100]} tick={{ fontSize: 12 }} />
                      <Tooltip formatter={(value: number) => [`${value}%`, 'Match Rate']} />
                      <Bar dataKey="pct" radius={[6, 6, 0, 0]}>
                        {matchQualityData.map((entry, i) => (
                          <Cell key={i} fill={matchBarColor(entry.pct)} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Traffic Source Table */}
          {data.source_funnel && data.source_funnel.length > 0 && (
            <Card>
              <CardHeader><CardTitle className="text-base flex items-center gap-1.5">Traffic Sources — Funnel Breakdown <InfoTip text="Funnel breakdown per traffic source showing visits through to purchases, with end-to-end conversion rate." /></CardTitle></CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="text-left">Source</TableHead>
                      <TableHead className="text-right">PageView</TableHead>
                      <TableHead className="text-right">ViewContent</TableHead>
                      <TableHead className="text-right">AddToCart</TableHead>
                      <TableHead className="text-right">Checkout</TableHead>
                      <TableHead className="text-right">Purchase</TableHead>
                      <TableHead className="text-right">Conv%</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.source_funnel.map((row, i) => {
                      const conv = row.PageView > 0 ? ((row.Purchase / row.PageView) * 100).toFixed(1) : '0.0';
                      return (
                        <TableRow key={row.source} className={i % 2 === 0 ? 'bg-muted/30' : ''}>
                          <TableCell className="text-left">
                            <div className="flex items-center gap-2">
                              <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: SOURCE_COLORS[row.source] || SOURCE_COLORS.unknown }} />
                              <span className="font-medium">{getSourceLabel(row.source)}</span>
                            </div>
                          </TableCell>
                          <TableCell className="text-right font-mono">{fmtNum(row.PageView)}</TableCell>
                          <TableCell className="text-right font-mono">{fmtNum(row.ViewContent)}</TableCell>
                          <TableCell className="text-right font-mono">{fmtNum(row.AddToCart)}</TableCell>
                          <TableCell className="text-right font-mono">{fmtNum(row.InitiateCheckout)}</TableCell>
                          <TableCell className="text-right font-mono">{fmtNum(row.Purchase)}</TableCell>
                          <TableCell className={`text-right font-mono ${parseFloat(conv) > 0 ? 'text-green-600' : 'text-muted-foreground'}`}>
                            {conv}%
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}

          {/* Country + Heatmap */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {data.country_breakdown && data.country_breakdown.length > 0 && (
              <Card>
                <CardHeader><CardTitle className="text-base flex items-center gap-2"><Globe className="h-4 w-4" /> Country Breakdown <InfoTip text="Top 10 countries by deduplicated event count. Detected from IP geolocation data sent with server-side events." /></CardTitle></CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={260}>
                    <BarChart data={data.country_breakdown.slice(0, 10)} layout="vertical" margin={{ left: 20, right: 20 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(20, 10%, 90%)" />
                      <XAxis type="number" tick={{ fontSize: 12 }} />
                      <YAxis type="category" dataKey="code" tick={{ fontSize: 12 }} width={50} tickFormatter={(code: string) => `${COUNTRY_FLAGS[code] || ''} ${code}`} />
                      <Tooltip formatter={(value: number) => [fmtNum(value), 'Events']} />
                      <Bar dataKey="count" radius={[0, 6, 6, 0]}>
                        {data.country_breakdown.slice(0, 10).map((_, i) => (
                          <Cell key={i} fill={COUNTRY_COLORS[i % COUNTRY_COLORS.length]} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            )}

            {hourlyHeatmap.length > 0 && (
              <Card>
                <CardHeader><CardTitle className="text-base flex items-center gap-1.5">Activity Heatmap (by Hour) <InfoTip text="Events distributed by hour of day (local time). Darker = more activity. Helps identify peak engagement hours." /></CardTitle></CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-1.5">
                    {hourlyHeatmap.map(h => (
                      <div key={h.hour} className="flex flex-col items-center gap-1" title={`${h.hour}:00 — ${fmtNum(h.count)} events`}>
                        <div
                          className="w-8 h-8 rounded-md flex items-center justify-center text-[10px] font-mono font-medium"
                          style={{
                            backgroundColor: h.intensity > 0.7
                              ? `hsl(350, 40%, ${65 - h.intensity * 25}%)`
                              : h.intensity > 0.3
                              ? `hsl(45, ${60 + h.intensity * 30}%, ${75 - h.intensity * 15}%)`
                              : `hsl(210, 15%, ${95 - h.intensity * 15}%)`,
                            color: h.intensity > 0.5 ? 'white' : 'hsl(220, 12%, 28%)',
                          }}
                        >
                          {h.count > 0 ? h.count : ''}
                        </div>
                        <span className="text-[9px] text-muted-foreground">{h.hour}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {data.country_traffic && data.country_traffic.length > 0 && (
              <Card>
                <CardHeader><CardTitle className="text-base flex items-center gap-2"><Globe className="h-4 w-4" /> Traffic by Country <InfoTip text="Funnel breakdown per country showing visits through to purchases, with end-to-end conversion rate." /></CardTitle></CardHeader>
                <CardContent className="space-y-6">
                  <ResponsiveContainer width="100%" height={Math.max(200, data.country_traffic.length * 40)}>
                    <BarChart data={data.country_traffic} layout="vertical" margin={{ left: 50, right: 20 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(20, 10%, 90%)" />
                      <XAxis type="number" tick={{ fontSize: 12 }} />
                      <YAxis type="category" dataKey="code" tick={{ fontSize: 12 }} width={60} tickFormatter={(code: string) => `${COUNTRY_FLAGS[code] || ''} ${code}`} />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="visits" stackId="a" name="PageView" fill={FUNNEL_COLORS[0]} />
                      <Bar dataKey="viewers" stackId="a" name="ViewContent" fill={FUNNEL_COLORS[1]} />
                      <Bar dataKey="carts" stackId="a" name="AddToCart" fill={FUNNEL_COLORS[2]} />
                      <Bar dataKey="checkouts" stackId="a" name="Checkout" fill={FUNNEL_COLORS[3]} />
                      <Bar dataKey="purchases" stackId="a" name="Purchase" fill={FUNNEL_COLORS[4]} radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="text-left">Country</TableHead>
                        <TableHead className="text-right">Visits</TableHead>
                        <TableHead className="text-right">View</TableHead>
                        <TableHead className="text-right">Cart</TableHead>
                        <TableHead className="text-right">Checkout</TableHead>
                        <TableHead className="text-right">Purchase</TableHead>
                        <TableHead className="text-right">Conv%</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {data.country_traffic.map((row, i) => {
                        const conv = row.visits > 0 ? ((row.purchases / row.visits) * 100).toFixed(1) : '0.0';
                        return (
                          <TableRow key={row.code} className={i % 2 === 0 ? 'bg-muted/30' : ''}>
                            <TableCell className="text-left font-medium">{COUNTRY_FLAGS[row.code] || ''} {row.code}</TableCell>
                            <TableCell className="text-right font-mono">{fmtNum(row.visits)}</TableCell>
                            <TableCell className="text-right font-mono">{fmtNum(row.viewers)}</TableCell>
                            <TableCell className="text-right font-mono">{fmtNum(row.carts)}</TableCell>
                            <TableCell className="text-right font-mono">{fmtNum(row.checkouts)}</TableCell>
                            <TableCell className="text-right font-mono">{fmtNum(row.purchases)}</TableCell>
                            <TableCell className={`text-right font-mono ${parseFloat(conv) > 0 ? 'text-green-600' : 'text-muted-foreground'}`}>
                              {conv}%
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Table 1: Event Volume */}
          <Card>
            <CardHeader><CardTitle className="text-base flex items-center gap-1.5">Event Volume <InfoTip text="Detailed table of deduplicated events. Browser = client-side firing confirmed. Server = CAPI/MP firing confirmed. Conv. Rate = step-to-step conversion from previous funnel stage." /></CardTitle></CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-left">Event</TableHead>
                    <TableHead className="text-right">Total</TableHead>
                    <TableHead className="text-right">Browser</TableHead>
                    <TableHead className="text-right">Server</TableHead>
                    <TableHead className="text-right">Value</TableHead>
                    <TableHead className="text-right">Conv. Rate</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.volume.map((row, i) => (
                    <TableRow key={row.event} className={i % 2 === 0 ? 'bg-muted/30' : ''}>
                      <TableCell className="text-left font-medium">{row.event}</TableCell>
                      <TableCell className="text-right font-mono">{fmtNum(row.total)}</TableCell>
                      <TableCell className="text-right font-mono">{fmtNum(row.browser)}</TableCell>
                      <TableCell className="text-right font-mono">{fmtNum(row.server + row.webhook)}</TableCell>
                      <TableCell className="text-right font-mono">{row.totalValue > 0 ? fmtVal(row.totalValue) : '—'}</TableCell>
                      <TableCell className="text-right font-mono">
                        {row.conversionRate !== null ? `${row.conversionRate}%` : '—'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          {/* Table 2: FB CAPI Match Quality */}
          <Card>
            <CardHeader><CardTitle className="text-base flex items-center gap-1.5">Facebook CAPI Match Quality <InfoTip text="Per-event breakdown of all data parameters sent to Meta CAPI. Higher percentages mean better ad matching. PII fields are SHA-256 hashed for privacy." /></CardTitle></CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-left">Event</TableHead>
                    <TableHead className="text-right">Count</TableHead>
                    <TableHead className="text-right">Email%</TableHead>
                    <TableHead className="text-right">Phone%</TableHead>
                    <TableHead className="text-right">FN%</TableHead>
                    <TableHead className="text-right">LN%</TableHead>
                    <TableHead className="text-right">City%</TableHead>
                    <TableHead className="text-right">State%</TableHead>
                    <TableHead className="text-right">Zip%</TableHead>
                    <TableHead className="text-right">Country%</TableHead>
                    <TableHead className="text-right">Gender%</TableHead>
                    <TableHead className="text-right">IP%</TableHead>
                    <TableHead className="text-right">UA%</TableHead>
                    <TableHead className="text-right">IPv6%</TableHead>
                    <TableHead className="text-right">fbp%</TableHead>
                    <TableHead className="text-right">fbc%</TableHead>
                    <TableHead className="text-right">ext_id%</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.fb_match_quality.map((row, i) => (
                    <TableRow key={row.event} className={i % 2 === 0 ? 'bg-muted/30' : ''}>
                      <TableCell className="text-left font-medium">{row.event}</TableCell>
                      <TableCell className="text-right font-mono">{fmtNum(row.count)}</TableCell>
                      <TableCell className={`text-right font-mono ${matchColor(row.email_pct)}`}>{row.email_pct}%</TableCell>
                      <TableCell className={`text-right font-mono ${matchColor(row.phone_pct)}`}>{row.phone_pct}%</TableCell>
                      <TableCell className={`text-right font-mono ${matchColor(row.fn_pct ?? 0)}`}>{row.fn_pct ?? 0}%</TableCell>
                      <TableCell className={`text-right font-mono ${matchColor(row.ln_pct ?? 0)}`}>{row.ln_pct ?? 0}%</TableCell>
                      <TableCell className={`text-right font-mono ${matchColor(row.ct_pct ?? 0)}`}>{row.ct_pct ?? 0}%</TableCell>
                      <TableCell className={`text-right font-mono ${matchColor(row.st_pct ?? 0)}`}>{row.st_pct ?? 0}%</TableCell>
                      <TableCell className={`text-right font-mono ${matchColor(row.zp_pct ?? 0)}`}>{row.zp_pct ?? 0}%</TableCell>
                      <TableCell className={`text-right font-mono ${matchColor(row.country_pct ?? 0)}`}>{row.country_pct ?? 0}%</TableCell>
                      <TableCell className={`text-right font-mono ${matchColor(row.ge_pct ?? 0)}`}>{row.ge_pct ?? 0}%</TableCell>
                      <TableCell className={`text-right font-mono ${matchColor(row.ip_pct)}`}>{row.ip_pct}%</TableCell>
                      <TableCell className={`text-right font-mono ${matchColor(row.ua_pct ?? 0)}`}>{row.ua_pct ?? 0}%</TableCell>
                      <TableCell className={`text-right font-mono ${matchColor(row.ipv6_pct)}`}>{row.ipv6_pct}%</TableCell>
                      <TableCell className={`text-right font-mono ${matchColor(row.fbp_pct)}`}>{row.fbp_pct}%</TableCell>
                      <TableCell className={`text-right font-mono ${matchColor(row.fbc_pct)}`}>{row.fbc_pct}%</TableCell>
                      <TableCell className={`text-right font-mono ${matchColor(row.external_id_pct)}`}>{row.external_id_pct}%</TableCell>
                    </TableRow>
                  ))}
                  {data.fb_match_quality.length === 0 && (
                    <TableRow><TableCell colSpan={17} className="text-center text-muted-foreground">No data yet</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
              </div>
            </CardContent>
          </Card>

          {/* Table 3: GA4 MP */}
          <Card>
            <CardHeader><CardTitle className="text-base flex items-center gap-1.5">Google Analytics MP <InfoTip text="Per-event breakdown of GA4 Measurement Protocol parameters. client_id links to the GA4 cookie; session_id links to the active session." /></CardTitle></CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-left">Event</TableHead>
                    <TableHead className="text-right">Count</TableHead>
                    <TableHead className="text-right">client_id%</TableHead>
                    <TableHead className="text-right">session_id%</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.ga4.map((row, i) => (
                    <TableRow key={row.event} className={i % 2 === 0 ? 'bg-muted/30' : ''}>
                      <TableCell className="text-left font-medium">{row.event}</TableCell>
                      <TableCell className="text-right font-mono">{fmtNum(row.count)}</TableCell>
                      <TableCell className={`text-right font-mono ${matchColor(row.client_id_pct)}`}>{row.client_id_pct}%</TableCell>
                      <TableCell className={`text-right font-mono ${matchColor(row.session_id_pct)}`}>{row.session_id_pct}%</TableCell>
                    </TableRow>
                  ))}
                  {data.ga4.length === 0 && (
                    <TableRow><TableCell colSpan={4} className="text-center text-muted-foreground">No data yet</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          {/* Table 4: Custom Events */}
          {data.custom_events.length > 0 && (
            <Card>
              <CardHeader><CardTitle className="text-base flex items-center gap-1.5">Custom Events <InfoTip text="Additional tracked events outside the standard funnel (e.g. PurchaseNewCustomer, PurchaseReturnCustomer)." /></CardTitle></CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="text-left">Event</TableHead>
                      <TableHead className="text-right">Count</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.custom_events.map((row, i) => (
                      <TableRow key={row.event} className={i % 2 === 0 ? 'bg-muted/30' : ''}>
                        <TableCell className="text-left font-medium">{row.event}</TableCell>
                        <TableCell className="text-right font-mono">{fmtNum(row.count)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}

          {/* Event Log */}
          <EventLogSection data={data} fmtVal={fmtVal} />
        </>
      )}
    </div>
  );
}
